import { Component, OnInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { ChatService } from '../../services/chat.service';
import { ChatMessage, TableData } from '../../models/chat-message.interface';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, PickerComponent],
  providers: [ChatService],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit {
  @ViewChild('messagesContainer') private messagesContainerRef?: ElementRef<HTMLDivElement>;
  conversationId: string = '';
  schemaId: string = '';
  token: string = '';
	selectedOrg: string = '';
  moduleSlugName: string = '';
  email: string = '';
  environmentName: string = '';
  question: string = '';
  loading: boolean = false;
  error: string = '';
  messages: ChatMessage[] = [];
  showEmojiPicker = false;
  isEmbedded: boolean = true;

  constructor(private chatService: ChatService) {}

  @HostListener('window:storage', ['$event'])
  onStorageChange(event: StorageEvent) {
    if (event.key === 'schemaId' && event.newValue) {
      this.schemaId = event.newValue;
    }
  }

  private checkIfEmbedded(): boolean {
    try {
      const isInIframe = window !== window.parent;
      
      if (window.top === window) {
        console.warn('Chat accessed directly - not embedded in iframe');
        return false;
      }
      
      const parentOrigin = window.parent.location.origin;
      const allowedOrigins = [
        'http://localhost:4200',
        'http://localhost:4201',
        'https://servitzhome-dev.fritz.co.il',
        'https://servitzhome-test.fritz.co.il',
        'https://servitzhome.fritz.co.il'
      ];
      
      const isAllowedParent = allowedOrigins.some(origin => {
        if (origin.startsWith('*.')) {
          const domain = origin.substring(2);
          return parentOrigin.endsWith(domain);
        }
        return parentOrigin === origin;
      });
      
      if (!isAllowedParent) {
        console.warn(`Chat embedded in unauthorized parent: ${parentOrigin}`);
        return false;
      }
      
      return isInIframe && isAllowedParent;
    } catch (error) {
      console.log('Cross-origin iframe detected - assuming embedded', error);
      return true;
    }
  }

  private detectEnvironmentFromParent(): string {
    try {
      // Try to get parent origin from referrer first
      const referrer = document.referrer;
      if (referrer) {
        if (referrer.includes('servitzhome-dev.fritz.co.il')) {
          return 'dev';
        } else if (referrer.includes('servitzhome-test.fritz.co.il')) {
          return 'test';
        } else if (referrer.includes('servitzhome.fritz.co.il')) {
          return 'prod';
        } else if (referrer.includes('localhost:4200') || referrer.includes('localhost:4201')) {
          return 'dev';
        }
      }

      // Fallback: try to access parent location (will fail for cross-origin)
      try {
        const parentOrigin = window.parent.location.origin;
        if (parentOrigin.includes('servitzhome-dev.fritz.co.il')) {
          return 'dev';
        } else if (parentOrigin.includes('servitzhome-test.fritz.co.il')) {
          return 'test';
        } else if (parentOrigin.includes('servitzhome.fritz.co.il')) {
          return 'prod';
        } else if (parentOrigin.includes('localhost')) {
          return 'dev';
        }
      } catch (e) {
        // Cross-origin access blocked, already handled by referrer above
      }
    } catch (error) {
      console.warn('Could not detect environment from parent, defaulting to dev', error);
    }

    return 'dev'; // Default fallback
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.showEmojiPicker = false;
  }

  toggleEmojiPicker(event: Event) {
    event.stopPropagation();
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(event: any) {
    this.question += event.emoji.native;
  }

  ngOnInit() {
    this.isEmbedded = this.checkIfEmbedded();
    
    if (!this.isEmbedded) {
      console.error('Chat component accessed directly - security violation');
      return;
    }

    window.addEventListener('message', (event) => {  
      const allowedOrigins = [
        'http://localhost:4200',
        'http://localhost:4201',
        'https://servitzhome-dev.fritz.co.il',
        'https://servitzhome-test.fritz.co.il',
        'https://servitzhome.fritz.co.il'
      ];
	    console.log('event.data',event.data);
      if (
        allowedOrigins.includes(event.origin) &&
        event.data?.type === 'set-schema-id'
      ) {
        this.schemaId = event.data.schemaId;
        this.token = event.data.token || '';
        this.selectedOrg = event.data.selectedOrg || '';
        this.moduleSlugName = event.data.moduleSlugName || '';
        this.email = event.data.email || '';
        // Use provided environmentName or detect from parent domain
        this.environmentName = event.data.environmentName || this.detectEnvironmentFromParent();
        localStorage.setItem('schemaId', this.schemaId);
        localStorage.setItem('token', this.token);
        localStorage.setItem('moduleSlugName', this.moduleSlugName);
        localStorage.setItem('selectedOrg', this.selectedOrg);
        localStorage.setItem('email', this.email);
        localStorage.setItem('environmentName', this.environmentName);

        // console.log('Data set from postMessage:', {
        //   schemaId: this.schemaId,
	      //   selectedOrg:  this.selectedOrg,
        //   token: this.token ? '[PRESENT]' : '[MISSING]',
        //   moduleSlugName: this.moduleSlugName,
        //   environmentName: this.environmentName
        // });
  
        if (!this.conversationId) {
          this.startNewConversation();
        }
      }
    });
  
    setTimeout(() => {
      if (!this.schemaId) {
        this.schemaId = localStorage.getItem('schemaId') || uuidv4();
        this.token = localStorage.getItem('token') || '';
        this.moduleSlugName = localStorage.getItem('moduleSlugName') || '';
        this.selectedOrg = localStorage.getItem('selectedOrg') || '';
        this.email = localStorage.getItem('email') || '';
        // Use stored environmentName or detect from parent domain
        // this.environmentName = localStorage.getItem('environmentName');
        this.environmentName = this.detectEnvironmentFromParent();

        if (!localStorage.getItem('schemaId')) {
          localStorage.setItem('schemaId', this.schemaId);
        }
        
        // console.log('Data fallback to local:', {
        //   schemaId: this.schemaId,
        //   token: this.token ? '[PRESENT]' : '[MISSING]',
        //   moduleSlugName: this.moduleSlugName,
	      //   selectedOrg: this.selectedOrg,
        //   environmentName: this.environmentName
        // });
        
        this.startNewConversation();
      }
    }, 1000);
  }
  
  private scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    const container = this.messagesContainerRef?.nativeElement;
    if (!container) return;
    try {
      container.scrollTo({ top: container.scrollHeight, behavior });
    } catch {
      container.scrollTop = container.scrollHeight;
    }
  }

  private scheduleScrollToBottom(): void {
    requestAnimationFrame(() => this.scrollToBottom('smooth'));
  }

  

  startNewConversation() {
    // console.log('Starting new conversation with data:', {
    //   schemaId: this.schemaId,
    //   token: this.token ? '[PRESENT]' : '[MISSING]',
    //   moduleSlugName: this.moduleSlugName,
    //   selectedOrg: this.selectedOrg,
    //   environmentName: this.environmentName
    // });
    
    this.loading = true;
    this.error = '';
    this.messages = [];

    this.chatService.startConversation(this.token, this.schemaId, this.moduleSlugName, this.selectedOrg, this.email, this.environmentName).subscribe({
      next: (response) => {
        this.conversationId = response.conversationId;
        
        if (response.welcomeMessage) {
          const welcomeMessage: ChatMessage = {
            role: 'bot',
            content: response.welcomeMessage,
            timestamp: new Date()
          };
          this.messages.push(welcomeMessage);
          this.scheduleScrollToBottom();
        }
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error starting conversation:', error);
        this.error = error.error?.message || 'Failed to start conversation. Please try again.';
        this.loading = false;
      }
    });
  }

  private formatTableHeaders(columns: string[]): { displayColumns: string[], originalColumns: string[] } {
    const displayColumns = columns.map(column =>
      column
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    );
    return { displayColumns, originalColumns: columns };
  }

  private isResponseTable(response: any): response is { answer: TableData } {
    const table = response?.answer;
    return (
      table &&
      typeof table === 'object' &&
      table.type === 'table' &&
      Array.isArray(table.columns) &&
      Array.isArray(table.rows)
    );
  }

  sendMessage() {
    if (!this.question.trim() || !this.conversationId) return;

    const questionText = this.question.trim();

    const userMessage: ChatMessage = {
      role: 'user',
      content: questionText,
      timestamp: new Date()
    };
    this.messages.push(userMessage);
    this.scheduleScrollToBottom();

    this.question = '';

    this.loading = true;
    this.error = '';

    this.chatService.sendMessage(this.conversationId, this.schemaId, this.token, this.moduleSlugName, this.environmentName, questionText, this.email).subscribe({
      next: (response) => {
        //console.log('Raw response from server:', JSON.stringify(response, null, 2));
        let botMessage: ChatMessage;

        if (this.isResponseTable(response)) {
          const table = response.answer;
          const { displayColumns, originalColumns } = this.formatTableHeaders(table.columns);

         // console.log('Table data:', { displayColumns, originalColumns, rows: table.rows });

          botMessage = {
            role: 'bot',
            content: {
              type: 'table',
              columns: displayColumns,
              originalColumns,
              rows: table.rows
            },
            timestamp: new Date(),
            question: questionText,
            reviewStatus: null
          };
        } else {
          let textContent = '';
          if (response.output) {
            textContent = response.output;
          } else if (typeof response.answer === 'object' && response.answer && 'output' in response.answer) {
            textContent = (response.answer as any).output;
          } else {
            textContent = typeof response.answer === 'string' ? response.answer : 'No valid response received.';
          }

         // console.log('Text content:', textContent);

          botMessage = {
            role: 'bot',
            content: textContent,
            timestamp: new Date(),
            question: questionText,
            reviewStatus: null
          };
        }

       // console.log('Final bot message:', JSON.stringify(botMessage, null, 2));
        this.messages = [...this.messages, botMessage];
        this.scheduleScrollToBottom();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error sending message:', error);
        this.error = 'Failed to send message. Please try again.';
        this.loading = false;
      }
    });
  }

  isTableContent(content: any): content is TableData {
    return content &&
      typeof content === 'object' &&
      content.type === 'table' &&
      Array.isArray(content.columns) &&
      Array.isArray(content.originalColumns) &&
      Array.isArray(content.rows);
  }

  isHebrewText(text: string): boolean {
    if (!text || typeof text !== 'string') return false;
    // Hebrew characters Unicode range: \u0590-\u05FF
    // Hebrew vowel points (nikkud): \u05B0-\u05C7
    const hebrewRegex = /[\u0590-\u05FF]/;
    return hebrewRegex.test(text);
  }

  getTextDirection(content: any): string {
    if (this.isTableContent(content)) {
      // For tables, check if any of the column headers or first row contains Hebrew
      const textToCheck = [
        ...content.columns,
        ...(content.rows.length > 0 ? Object.values(content.rows[0]) : [])
      ].join(' ');
      return this.isHebrewText(textToCheck) ? 'rtl' : 'ltr';
    }
    
    if (typeof content === 'string') {
      return this.isHebrewText(content) ? 'rtl' : 'ltr';
    }
    
    return 'rtl'; // Default to RTL for Hebrew interface
  }

  isBilingualWelcomeMessage(content: any): boolean {
    if (typeof content !== 'string') return false;
    
    // Check if it contains both Hebrew and English text
    const hasHebrew = this.isHebrewText(content);
    const hasEnglish = /[a-zA-Z]/.test(content);
    
    // Also check for specific welcome message patterns
    const isWelcomePattern = content.includes('👋') && 
                           (content.includes('שלום') || content.includes('Hi!')) &&
                           (content.includes('בוטי') || content.includes('Boti'));
    
    return hasHebrew && hasEnglish && isWelcomePattern;
  }

  splitBilingualMessage(content: string): { hebrew: string; english: string } {
    // Split by double newline or look for the English section starting with "👋 Hi!"
    const parts = content.split(/\n\s*\n/);
    
    if (parts.length >= 2) {
      const hebrew = parts[0].trim();
      const english = parts.slice(1).join('\n\n').trim();
      return { hebrew, english };
    }
    
    // Fallback: try to split by "👋 Hi!" pattern
    const englishStartIndex = content.indexOf('👋 Hi!');
    if (englishStartIndex > 0) {
      const hebrew = content.substring(0, englishStartIndex).trim();
      const english = content.substring(englishStartIndex).trim();
      return { hebrew, english };
    }
    
    // If we can't split properly, return as is
    return { hebrew: content, english: '' };
  }

  getSafeBilingualContent(content: any): { hebrew: string; english: string } {
    if (typeof content === 'string') {
      return this.splitBilingualMessage(content);
    }
    return { hebrew: '', english: '' };
  }

  private findFirstStrongDirection(text: string): 'rtl' | 'ltr' | 'auto' {
    if (!text) return 'auto';
    for (const char of text) {
      if (/[ -\u001F\u007F\s]/.test(char)) {
        continue; // skip control/whitespace
      }
      if (/[ -\u007E]/.test(char)) {
        // basic latin or punctuation
        if (/[A-Za-z]/.test(char)) return 'ltr';
        // neutral punctuation/digits -> keep looking
        continue;
      }
      if (/[\u0590-\u05FF]/.test(char)) return 'rtl';
      if (/[\u0600-\u06FF]/.test(char)) return 'rtl'; // Arabic range just in case
      // For any other scripts, default keep looking; browser auto can handle
    }
    return 'auto';
  }

  determineInputDir(text: string | null | undefined): 'rtl' | 'ltr' | 'auto' {
    if (!text) return 'auto';
    return this.findFirstStrongDirection(text);
  }

  getInputDirectionClass(): string {
    const dir = this.determineInputDir(this.question);
    if (dir === 'rtl') return 'direction-rtl';
    if (dir === 'ltr') return 'direction-ltr';
    // Fallback when no strong chars yet: prefer RTL for Hebrew UI if user typed any Hebrew later
    return this.isHebrewText(this.question) ? 'direction-rtl' : 'direction-ltr';
  }

  downloadTableAsExcel(tableData: TableData): void {
    try {
      // Prepare data for Excel export
      const worksheetData: any[] = [];
      
      // Add headers row using display column names
      worksheetData.push(tableData.columns);
      
      // Add data rows using original column keys
      tableData.rows.forEach(row => {
        const rowData = tableData.originalColumns.map(col => row[col] || '');
        worksheetData.push(rowData);
      });

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Set column widths for better presentation
      const columnWidths = tableData.columns.map(col => ({
        wch: Math.max(col.length, 15) // Minimum width of 15 characters
      }));
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

      // Generate Excel file and save
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        cellStyles: true 
      });
      
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Generate filename with current date
      const currentDate = new Date();
      const dateString = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `table-data-${dateString}-${Date.now()}.xlsx`;
      
      saveAs(blob, filename);
      
    } catch (error) {
      console.error('Error exporting table to Excel:', error);
      this.error = 'Failed to export table. Please try again.';
    }
  }

  submitReview(message: ChatMessage, isHelpful: boolean): void {
    if (!message.question || message.reviewStatus) return;

    // Get the answer as a string
    let answerText = '';
    if (typeof message.content === 'string') {
      answerText = message.content;
    } else if (this.isTableContent(message.content)) {
      // Save full table payload as JSON string
      try {
        answerText = JSON.stringify(message.content);
      } catch {
        answerText = 'Table response (failed to stringify)';
      }
    }

    // Update the review status immediately for UI feedback
    message.reviewStatus = isHelpful ? 'liked' : 'disliked';

    // Submit to server
    this.chatService.submitReview(this.email, message.question, answerText, isHelpful).subscribe({
      next: (response) => {
        console.log('Review submitted successfully:', response);
      },
      error: (error) => {
        console.error('Error submitting review:', error);
        // Revert the status on error
        message.reviewStatus = null;
        this.error = 'Failed to submit review. Please try again.';
      }
    });
  }
}
